/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"
import { Alert, Button } from "@duxton-web/ui"
import { ConfettiOutlined, DateUniversal } from "@duxton-web/icons"

import { useEcomMigrationDeadlineClock } from "ecommerce/hooks/useEcomMigrationDeadlineClock"
import { parseEcomMigrationTimestamp } from "ecommerce/utils/ecomMigration"
import { MexcoreapipbEcomMigrationStatus } from "generated/mexcoreapi-sellercenter.type"
import { formatDate } from "utils/date_formatter"

import styles from "./MigrationBanner.modules.css"

// 7 days in seconds: PENDING_REVIEW switches to the yellow warning variant
// once the remaining review time is at or below this threshold.
const REVIEW_WARNING_THRESHOLD_SECONDS = 604800

// Temporary English copy (BP-63346) — replace with PhraseApp keys once the
// approved copy ships remotely. Kept in one object so the swap is mechanical.
const COPY = {
  blueTitle: "Your new catalogue suggestions",
  blueDescriptionPrefix: "Please confirm by ",
  blueDescriptionSuffix: " before we update your catalogue automatically.",
  yellowReviewTitle: "Auto catalogue update is scheduled on",
  yellowReviewDescription: "Review the suggested catalogue before the update",
  scheduledMessage: "Your catalogue migration has already been scheduled",
  review: "Review",
  learnMore: "Learn more",
}

export interface MigrationBannerProps {
  reviewEndAt: string
  status: MexcoreapipbEcomMigrationStatus
  onReview: () => void
  onLearnMore: () => void
}

export const MigrationBanner: FC<MigrationBannerProps> = ({
  reviewEndAt,
  status,
  onReview,
  onLearnMore,
}) => {
  const deadlineMs = parseEcomMigrationTimestamp(reviewEndAt)
  const { nowMs } = useEcomMigrationDeadlineClock({
    deadlineMs,
    transitionBeforeDeadlineMs:
      status === MexcoreapipbEcomMigrationStatus.PENDING_REVIEW
        ? REVIEW_WARNING_THRESHOLD_SECONDS * 1000
        : undefined,
  })

  if (deadlineMs === null || deadlineMs <= nowMs) {
    return null
  }

  if (status === MexcoreapipbEcomMigrationStatus.PENDING_REVIEW) {
    const remainingSeconds = (deadlineMs - nowMs) / 1000
    const isExpiringSoon = remainingSeconds <= REVIEW_WARNING_THRESHOLD_SECONDS
    const deadlineText = formatDate(new Date(deadlineMs), "DD MMMM YYYY")

    return (
      <Alert
        className={styles.banner}
        type={isExpiringSoon ? "warning" : "info"}
        showIcon
        icon={
          isExpiringSoon ? (
            <DateUniversal className={styles.icon} />
          ) : (
            <ConfettiOutlined className={styles.icon} />
          )
        }
        message={
          isExpiringSoon
            ? `${COPY.yellowReviewTitle} ${deadlineText}`
            : COPY.blueTitle
        }
        description={
          isExpiringSoon ? (
            COPY.yellowReviewDescription
          ) : (
            <span>
              {COPY.blueDescriptionPrefix}
              <strong className={styles.boldDate}>{deadlineText}</strong>
              {COPY.blueDescriptionSuffix}
            </span>
          )
        }
        action={
          <Button
            type="text"
            shape="round"
            className={styles.action}
            onClick={onReview}
          >
            {COPY.review}
          </Button>
        }
      />
    )
  }

  if (
    status === MexcoreapipbEcomMigrationStatus.SCHEDULING ||
    status === MexcoreapipbEcomMigrationStatus.SCHEDULED ||
    status === MexcoreapipbEcomMigrationStatus.IN_PROGRESS ||
    status === MexcoreapipbEcomMigrationStatus.COMPLETED
  ) {
    // No completed-state design yet, so COMPLETED temporarily shares the
    // scheduled-message variant.
    return (
      <Alert
        className={styles.banner}
        type="warning"
        showIcon
        icon={<DateUniversal className={styles.icon} />}
        message={COPY.scheduledMessage}
        action={
          <Button
            type="text"
            shape="round"
            className={styles.action}
            onClick={onLearnMore}
          >
            {COPY.learnMore}
          </Button>
        }
      />
    )
  }

  // ECOM_MIGRATION_STATUS_UNSPECIFIED, NOT_READY, and any unrecognized
  // runtime status fail closed to nothing.
  return null
}
